import { I18nextProvider } from 'react-i18next'
import { Provider as ReduxProvider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { ApolloProvider } from '@apollo/client'
import { BrowserRouter, Routes, Route as ReactRouterRoute } from 'react-router-dom'
import CssBaseline from '@mui/material/CssBaseline'

import { GoogleMapProvider } from '../modules/googleMaps'
import i18n from '../modules/i18n'
import { ThemeProvider } from '../modules/theme'
import client from '../modules/graphQl/core/client'
import { ScrollRestauration } from '../modules/navigation'
import { Cookieyes } from '../modules/consent'
import { GoogleAnalytics, FacebookPixel, RedditPixel } from '../modules/analytics'
import { NewRelic } from '../modules/monitoring'
import {
  StripeLandingPage,
  PaymentReceivedPage,
} from '../modules/payment/gateway/stripe'
import { useConfig } from '../modules/config'
import Route from './Route.enum'
import store, { persistor } from '../store'
import ErrorBoundary from '../components/errors/ErrorBoundary'
import { ToastContainer } from '../components/ToastNotifications'
import HomeDispatcherPage from '../pages/HomeDispatcherPage'
import BookingTermsPage from '../pages/bookingTerms'
import BookingTermsLongDistancePage from '../pages/bookingTermsLongDistance'
import TermsOfUsePage from '../pages/termsOfUse'
import PrivacyPolicyPage from '../pages/privacyPolicy'
import { Error403Page, Error404Page, Error500Page } from '../pages/errors'
import QuotesRequestPage from '../pages/quotes/QuotesRequestPage'
import {
  MoveGatePage,
  MoveDispatcherPage,
  EditMovePage,
  PayBalancePage,
  RatingPage,
} from '../pages/move'
import MoveReminderPage from '../pages/moveReminder'
import LanguageSwitcher from './LanguageSwitcher'
import MuiDatePickerProvider from './MuiDatePickerProvider'
import AppGate from './AppGate'

const App: React.FC = () => {
  const languages = Object.keys(useConfig('i18n.locales'))

  return (
    <I18nextProvider i18n={i18n}>
      <MuiDatePickerProvider>
        <ReduxProvider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            { () => (
              <ApolloProvider client={client}>
                <AppGate>
                  <ThemeProvider>
                    <GoogleMapProvider>

                      <CssBaseline />

                      <BrowserRouter>
                        <ErrorBoundary>
                          <Routes>
                            { languages.map(language => (
                              <ReactRouterRoute
                                key={language}
                                path={`/${language}/*`}
                                element={<LanguageSwitcher />}
                              />
                            )) }

                            <ReactRouterRoute path={Route.HomePage} element={<HomeDispatcherPage />} />
                            <ReactRouterRoute path={Route.BookingTerms} element={<BookingTermsPage />} />
                            <ReactRouterRoute path={Route.BookingTermsLongDistance} element={<BookingTermsLongDistancePage />} />
                            <ReactRouterRoute path={Route.TermsOfUse} element={<TermsOfUsePage />} />
                            <ReactRouterRoute path={Route.PrivacyPolicy} element={<PrivacyPolicyPage />} />

                            <ReactRouterRoute path={Route.QuotesRequest} element={<QuotesRequestPage />} />

                            <ReactRouterRoute path={Route.Move} element={<MoveGatePage />}>
                              <ReactRouterRoute path="" element={<MoveDispatcherPage />} />
                              <ReactRouterRoute path={Route.EditMove} element={<EditMovePage />} />
                            </ReactRouterRoute>
                            <ReactRouterRoute path={Route.Move} element={<MoveGatePage postMove />}>
                              <ReactRouterRoute path={Route.PayBalance} element={<PayBalancePage />} />
                              <ReactRouterRoute path={Route.StripeLandingPage} element={<StripeLandingPage />} />
                              <ReactRouterRoute path={Route.PaymentReceivedPage} element={<PaymentReceivedPage />} />
                              <ReactRouterRoute path={Route.Rating} element={<RatingPage />} />
                            </ReactRouterRoute>

                            <ReactRouterRoute path={Route.MoveReminder} element={<MoveReminderPage />} />

                            <ReactRouterRoute path="/403" element={<Error403Page />} />
                            <ReactRouterRoute path="/500" element={<Error500Page />} />
                            <ReactRouterRoute path="*" element={<Error404Page />} />
                          </Routes>

                          <Cookieyes />
                          <GoogleAnalytics />
                          <FacebookPixel />
                          <RedditPixel />
                          <NewRelic />
                          <ScrollRestauration />
                          <ToastContainer />
                        </ErrorBoundary>
                      </BrowserRouter>
                    </GoogleMapProvider>
                  </ThemeProvider>
                </AppGate>
              </ApolloProvider>
            ) }
          </PersistGate>
        </ReduxProvider>
      </MuiDatePickerProvider>
    </I18nextProvider>
  )
}

export default App
